import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDownUp, Share2 } from 'lucide-react';

import {
  useLookupLolQuery,
  useLookupLorQuery,
  useLookupTftQuery,
  useMyAccountsQuery,
} from '@/services/qriotsApi';
import { classifyQriotsError } from '@/services/qriotsErrors';
import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';
import type { GameCode, Platform, ProfileCard } from '@/types/qriots';
import { Loader } from '@/components/common/Loader';
import {
  CompareSideInput,
  type CompareSideValue,
} from '@/components/riot/CompareSideInput';
import { ComparedProfile } from '@/components/riot/ComparedProfile';
import {
  computeDeltas,
  type ComputedDeltas,
} from '@/components/riot/compareDeltas';

const TAGLINE_RE = /^[A-Za-z0-9]{3,5}$/;

function isSideValid(v: CompareSideValue): boolean {
  return (
    v.gameName.trim().length > 0 &&
    TAGLINE_RE.test(v.tagLine.trim()) &&
    !!v.region
  );
}

function readSideFromUrl(
  params: URLSearchParams,
  prefix: 'a' | 'b',
): CompareSideValue {
  return {
    gameName: params.get(`${prefix}Name`) ?? '',
    tagLine: params.get(`${prefix}Tag`) ?? '',
    region: (params.get(`${prefix}Reg`) as Platform | null) ?? undefined,
  };
}

function writeStateToParams(
  game: GameCode,
  sideA: CompareSideValue,
  sideB: CompareSideValue,
): Record<string, string> {
  return {
    game,
    aName: sideA.gameName,
    aTag: sideA.tagLine,
    aReg: sideA.region ?? '',
    bName: sideB.gameName,
    bTag: sideB.tagLine,
    bReg: sideB.region ?? '',
  };
}

export function RiotCompare() {
  const { t } = useTranslation('riot');
  const haptics = useHaptics();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial state derived from URL.
  const initialGame = (searchParams.get('game') as GameCode) ?? 'lol';
  const [game, setGame] = useState<GameCode>(initialGame);
  const [sideA, setSideA] = useState<CompareSideValue>(() =>
    readSideFromUrl(searchParams, 'a'),
  );
  const [sideB, setSideB] = useState<CompareSideValue>(() =>
    readSideFromUrl(searchParams, 'b'),
  );

  // Auto-commit when the URL on first load already carries a complete pair.
  const initiallyCommitted =
    isSideValid(readSideFromUrl(searchParams, 'a')) &&
    isSideValid(readSideFromUrl(searchParams, 'b'));
  const [committed, setCommitted] = useState(initiallyCommitted);

  // Side-A prefill from saved accounts, only when URL doesn't override.
  const myAccounts = useMyAccountsQuery();
  useEffect(() => {
    if (sideA.gameName) return;
    const accounts = myAccounts.data?.accounts ?? [];
    if (accounts.length === 0) return;
    const explicit = searchParams.has('game');
    const candidate = explicit
      ? (accounts.find((a) => a.game === game) ?? null)
      : (accounts[0] ?? null);
    if (!candidate) return;
    if (!explicit && candidate.game !== game) {
      setGame(candidate.game as GameCode);
    }
    setSideA({
      gameName: candidate.riotId.gameName,
      tagLine: candidate.riotId.tagLine,
      region: candidate.region as Platform,
    });
  }, [myAccounts.data, sideA.gameName, game, searchParams]);

  const bothValid = isSideValid(sideA) && isSideValid(sideB);

  // Skip flags: only the hook pair matching current `game` ever fetches.
  const skipLol = !committed || game !== 'lol' || !bothValid;
  const skipTft = !committed || game !== 'tft' || !bothValid;
  const skipLor = !committed || game !== 'lor' || !bothValid;

  // EUW1 is a harmless placeholder when the hook is skipped.
  const fallbackRegion: Platform = 'EUW1';

  const lolA = useLookupLolQuery(
    {
      gameName: sideA.gameName,
      tagLine: sideA.tagLine,
      region: sideA.region ?? fallbackRegion,
    },
    { skip: skipLol },
  );
  const lolB = useLookupLolQuery(
    {
      gameName: sideB.gameName,
      tagLine: sideB.tagLine,
      region: sideB.region ?? fallbackRegion,
    },
    { skip: skipLol },
  );
  const tftA = useLookupTftQuery(
    {
      gameName: sideA.gameName,
      tagLine: sideA.tagLine,
      region: sideA.region ?? fallbackRegion,
    },
    { skip: skipTft },
  );
  const tftB = useLookupTftQuery(
    {
      gameName: sideB.gameName,
      tagLine: sideB.tagLine,
      region: sideB.region ?? fallbackRegion,
    },
    { skip: skipTft },
  );
  const lorA = useLookupLorQuery(
    {
      gameName: sideA.gameName,
      tagLine: sideA.tagLine,
      region: sideA.region ?? fallbackRegion,
    },
    { skip: skipLor },
  );
  const lorB = useLookupLorQuery(
    {
      gameName: sideB.gameName,
      tagLine: sideB.tagLine,
      region: sideB.region ?? fallbackRegion,
    },
    { skip: skipLor },
  );

  // Extract per-side fields from whichever game's hook is active. We unpack
  // field-by-field instead of unioning the three hook results, because TS
  // can't unify their generic types cleanly (the union widens .isLoading /
  // .error to `unknown` even though structurally they're identical).
  const pickA = <K extends keyof typeof lolA>(k: K) =>
    game === 'lol' ? lolA[k] : game === 'tft' ? tftA[k] : lorA[k];
  const pickB = <K extends keyof typeof lolB>(k: K) =>
    game === 'lol' ? lolB[k] : game === 'tft' ? tftB[k] : lorB[k];

  const aData = pickA('data') as ProfileCard | undefined;
  const bData = pickB('data') as ProfileCard | undefined;
  const aError = pickA('error');
  const bError = pickB('error');
  const aIsLoading = pickA('isLoading') as boolean;
  const bIsLoading = pickB('isLoading') as boolean;
  const aIsFetching = pickA('isFetching') as boolean;
  const bIsFetching = pickB('isFetching') as boolean;
  const aRefetch = pickA('refetch') as () => void;
  const bRefetch = pickB('refetch') as () => void;

  const deltas: ComputedDeltas = useMemo(() => {
    if (aData && bData) return computeDeltas(aData, bData, game);
    return { A: [], B: [], ties: [] };
  }, [aData, bData, game]);

  const commit = () => {
    setSearchParams(writeStateToParams(game, sideA, sideB), { replace: true });
    setCommitted(true);
  };

  useMainButton({
    text: t('compare.cta'),
    enabled: bothValid,
    loading: committed && (aIsFetching || bIsFetching),
    onClick: commit,
  });

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      haptics.selection();
    } catch {
      // Clipboard may be denied in some WebViews. Silent no-op is acceptable.
    }
  };

  const sideAPrefilled =
    !searchParams.has('aName') &&
    !!sideA.gameName &&
    (myAccounts.data?.accounts ?? []).some(
      (acc) =>
        acc.riotId.gameName === sideA.gameName &&
        acc.riotId.tagLine === sideA.tagLine,
    );

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      <header className="mx-4 flex items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-fg">{t('compare.title')}</h1>
          <p className="text-sm text-fg-muted">{t('compare.subtitle')}</p>
        </div>
        {committed && (
          <button
            type="button"
            onClick={onShare}
            aria-label={t('compare.share')}
            className="rounded-full p-2 text-fg-muted active:opacity-80"
          >
            <Share2 aria-hidden className="h-4 w-4" />
          </button>
        )}
      </header>

      {/* Game selector */}
      <div className="mx-4 flex gap-2">
        {(['lol', 'tft', 'lor'] as const).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => {
              setGame(g);
              setCommitted(false);
            }}
            className={
              'flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide ' +
              (game === g
                ? 'bg-brand text-brand-fg'
                : 'bg-bg-secondary text-fg-muted')
            }
          >
            {g.toUpperCase()}
          </button>
        ))}
      </div>

      <CompareSideInput
        label={t('compare.sideA')}
        sublabel={sideAPrefilled ? t('compare.sideAPrefilled') : undefined}
        value={sideA}
        onChange={(next) => {
          setSideA(next);
          setCommitted(false);
        }}
      />

      <div className="mx-4 flex items-center gap-2 text-fg-muted">
        <ArrowDownUp aria-hidden className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wider">vs</span>
      </div>

      <CompareSideInput
        label={t('compare.sideB')}
        value={sideB}
        onChange={(next) => {
          setSideB(next);
          setCommitted(false);
        }}
      />

      {committed && (aIsLoading || bIsLoading) ? (
        <div className="flex h-64 items-center justify-center">
          <Loader />
        </div>
      ) : null}

      {committed && (aData || aError) && (bData || bError) ? (
        <div className="flex flex-col gap-4">
          <ComparedProfile
            side="A"
            game={game}
            data={aData}
            error={aError ? classifyQriotsError(aError) : undefined}
            deltas={deltas}
            riotIdHint={`${sideA.gameName}#${sideA.tagLine}`}
            onRetry={() => aRefetch()}
          />
          <ComparedProfile
            side="B"
            game={game}
            data={bData}
            error={bError ? classifyQriotsError(bError) : undefined}
            deltas={deltas}
            riotIdHint={`${sideB.gameName}#${sideB.tagLine}`}
            onRetry={() => bRefetch()}
          />
        </div>
      ) : null}

      {!committed && (
        <p className="mx-4 text-center text-sm text-fg-muted">
          {!sideA.gameName && !sideB.gameName
            ? t('compare.empty.both')
            : String(
                t('compare.empty.oneSide', {
                  side: !sideA.gameName ? 'A' : 'B',
                }),
              )}
        </p>
      )}
    </div>
  );
}
